import React, { useEffect, useState } from 'react';
import { Form, FormikProvider, useFormik } from 'formik';
import {
  Box,
  Grid,
  Stack,
  TextField,
  Autocomplete,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import GlobalDialog from '../../components/mui/Dialogue/GlobalDialog';
import GlobalAllTypeFileUpload from '../../components/Fileupload/GlobalAllTypeFileUpload';
import GlobalAllTypeFileView from '../../components/Fileupload/GlobalAllTypeFileView';
import {
  updateTask,
  getAllTags,
  getAssignableGoals,
  getCreatedTasks,
  getTaskCategories,
  getTaskPriorities,
  getTasks,
} from '../../redux/features/taskManagementSlice';
import { getAlldepartments } from '../../redux/features/generalSlice';
import GlobalMultiSelectId from '../../components/mui/GlobalMultiSelectId';

const EditTask = ({ open, setOpen, task, filters }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { loading, priorities, taskCategories, assignableGoals, assignableGoalsLoading, allTags } = useSelector(
    (state) => state.taskManagement
  );
  const { alldepartments } = useSelector((state) => state.general);

  const currentUser = JSON.parse(localStorage.getItem('cred'))?.profile || {};

  // once somebody is assigned, re-assignment goes through the assignment dialog, not this form
  const isAlreadyAssigned =
    task?.task_assigned_to !== null && task?.task_assigned_to !== undefined && task?.task_assigned_to !== '';

  // PMS goal linkage for self-assign: same requirement as assigning someone
  // else - self-assigning IS creating an assignment.
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [creatingNewGoal, setCreatingNewGoal] = useState(false);
  const [newGoalName, setNewGoalName] = useState('');
  const [newGoalStartDate, setNewGoalStartDate] = useState('');
  const [newGoalEndDate, setNewGoalEndDate] = useState('');
  const [newGoalTagIds, setNewGoalTagIds] = useState([]);

  const validationSchema = Yup.object().shape({
    request_title: Yup.string().required('Task title is required'),
    request_body: Yup.string().required('Description is required'),
    target_dept_id: Yup.string().required('Target department is required'),
    priority: Yup.string().required('Priority is required'),
    expected_delivery_date: Yup.string().required('Expected delivery date is required'),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      request_title: task?.title || '',
      request_body: task?.description || '',
      request_link: task?.attachments || [],
      target_dept_id: task?.target_dept_id || '',
      priority: task?.priority || '',
      expected_delivery_date: task?.expected_delivery_date
        ? new Date(task.expected_delivery_date).toISOString().split('T')[0]
        : '',
      source_dept_id: task?.source_dept_id || '',
      category: task?.task_category_id || '',
      self_assign: false,
      // Cross-module tags - shared with PMS goals.
      tagIds: Array.isArray(task?.tags) ? task.tags.map((t) => t.id) : [],
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      // self assignment is only allowed for an unassigned task inside the user's own department
      const isOwnDept = String(values.target_dept_id) === String(currentUser?.departmentId);
      const willSelfAssign = isOwnDept && !isAlreadyAssigned && values.self_assign;

      if (willSelfAssign && !hasValidGoalSelection) {
        enqueueSnackbar('Pick or create a PMS goal to self-assign this task', { variant: 'error' });
        return;
      }

      const data = {
        task_id: task?.task_id,
        ...values,
        category: values.category === '' ? null : values.category,
        self_assign: willSelfAssign,
        ...(willSelfAssign
          ? selectedGoal
            ? { goal_id: selectedGoal.goal_id }
            : {
                newGoal: {
                  name: newGoalName,
                  startDate: newGoalStartDate,
                  endDate: newGoalEndDate,
                  tagIds: newGoalTagIds,
                },
              }
          : {}),
      };
      dispatch(updateTask(data)).then((res) => {
        if (res.payload?.status === 'success') {
          enqueueSnackbar(res.payload.message, { variant: 'success' });
          dispatch(getCreatedTasks(filters));
          dispatch(getTasks(filters));
          setOpen(false);
        } else {
          enqueueSnackbar(res.payload?.message || 'Update failed', { variant: 'error' });
        }
      });
    },
  });

  useEffect(() => {
    if (open) {
      dispatch(getAlldepartments());
      dispatch(getTaskPriorities());
      dispatch(getAllTags());
    }
  }, [open]);

  useEffect(() => {
    const deptId = formik.values.target_dept_id;
    if (deptId) {
      dispatch(getTaskCategories(deptId));
    }
  }, [formik.values.target_dept_id]);

  useEffect(() => {
    setSelectedGoal(null);
    setCreatingNewGoal(false);
    setNewGoalName('');
    setNewGoalStartDate('');
    setNewGoalEndDate('');
    setNewGoalTagIds([]);

    if (open && !isAlreadyAssigned && formik.values.self_assign && currentUser?.userId) {
      dispatch(getAssignableGoals(currentUser.userId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, formik.values.self_assign, dispatch]);

  useEffect(() => {
    if (formik.values.self_assign && !assignableGoalsLoading && assignableGoals?.length === 0) {
      setCreatingNewGoal(true);
    }
  }, [formik.values.self_assign, assignableGoalsLoading, assignableGoals]);

  const hasValidGoalSelection = Boolean(
    selectedGoal || (creatingNewGoal && newGoalName && newGoalStartDate && newGoalEndDate)
  );

  // PMS requires every job (and therefore every self-assigned task) to fall
  // within its goal's date range - mirrors the same rule already enforced
  // server-side (createGoalJobFromTask), shown here so the picker doesn't
  // let you set a due date that the backend will just reject afterward.
  // When a goal is selected, the goal's own start/end window is what's
  // enabled for the due date - not clamped to "today or later", since an
  // existing goal's window can legitimately start (or even end) before
  // today and every date inside it is still a valid due date.
  const selectedGoalStartDate = formik.values.self_assign
    ? selectedGoal?.goal_start_date || (creatingNewGoal ? newGoalStartDate : null)
    : null;
  const selectedGoalEndDate = formik.values.self_assign
    ? selectedGoal?.goal_end_date || (creatingNewGoal ? newGoalEndDate : null)
    : null;

  // the user can take the task himself only when the target department is his own department
  const isOwnDepartment =
    formik.values.target_dept_id !== '' &&
    String(formik.values.target_dept_id) === String(currentUser?.departmentId);

  // Parse attachments (array or comma-separated string)
  const attachmentList = Array.isArray(task?.attachments)
    ? task.attachments
    : typeof task?.attachments === 'string' && task.attachments.trim()
    ? task.attachments.split(',').map((a) => a.trim()).filter(Boolean)
    : [];

  return (
    <GlobalDialog
      title={'Edit Task'}
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth={'sm'}
      closeButton={true}
      content={
        <>
          <FormikProvider value={formik}>
            <Form onSubmit={formik.handleSubmit}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <TextField
                  label="Task Title"
                  name="request_title"
                  fullWidth
                  value={formik.values.request_title}
                  onChange={formik.handleChange}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  error={formik.touched.request_title && Boolean(formik.errors.request_title)}
                  helperText={formik.touched.request_title && formik.errors.request_title}
                />
                <TextField
                  label="Description"
                  name="request_body"
                  fullWidth
                  multiline
                  rows={3}
                  value={formik.values.request_body}
                  error={formik.touched.request_body && Boolean(formik.errors.request_body)}
                  helperText={formik.touched.request_body && formik.errors.request_body}
                  onChange={formik.handleChange}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                {attachmentList.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <GlobalAllTypeFileView attachmentFiles={attachmentList} previewButtonName={'View'} />
                  </Box>
                )}
                <GlobalAllTypeFileUpload
                  accept="image/*,video/*,audio/*,application/pdf"
                  formik={formik}
                  dbFolderName="task"
                  label="Attachments"
                  formikNameArray="request_link"
                  multiple
                />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12}>
                    <Autocomplete
                      name="target_dept_id"
                      fullWidth
                      options={alldepartments}
                      getOptionLabel={(option) => option.dept_name || ''}
                      onChange={(event, newValue) => {
                        const newDeptId = newValue?.dept_id || '';
                        formik.setFieldValue('target_dept_id', newDeptId);
                        // drop the self assign choice when the task is no longer for his own department
                        if (String(newDeptId) !== String(currentUser?.departmentId)) {
                          formik.setFieldValue('self_assign', false);
                        }
                      }}
                      value={alldepartments?.find((d) => d?.dept_id == formik.values.target_dept_id) || null}
                      renderInput={(params) => (
                        <TextField
                          fullWidth
                          autoComplete="off"
                          {...params}
                          label="Target Department"
                          error={formik.touched.target_dept_id && Boolean(formik.errors.target_dept_id)}
                          helperText={formik.touched.target_dept_id && formik.errors.target_dept_id}
                        />
                      )}
                    />
                  </Grid>
                  {isOwnDepartment && (
                    <Grid item xs={12} sm={12}>
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: formik.values.self_assign && !isAlreadyAssigned ? '#8B5CF6' : '#E0E0E0',
                          backgroundColor:
                            formik.values.self_assign && !isAlreadyAssigned ? 'rgba(139, 92, 246, 0.06)' : 'transparent',
                          borderRadius: 2,
                          px: 2,
                          py: 1,
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="self_assign"
                              disabled={isAlreadyAssigned}
                              checked={isAlreadyAssigned ? false : formik.values.self_assign}
                              onChange={(e) => formik.setFieldValue('self_assign', e.target.checked)}
                              sx={{ color: '#8B5CF6', '&.Mui-checked': { color: '#8B5CF6' } }}
                            />
                          }
                          label={
                            <Box>
                              <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Assign this task to myself</Typography>
                              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                                {isAlreadyAssigned
                                  ? `Already assigned to ${task?.assigned_to_name || 'an employee'}.`
                                  : formik.values.self_assign
                                  ? 'You can start working and update the status right away.'
                                  : 'Otherwise your reporting head has to assign this task before the status can be updated.'}
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: 'flex-start', m: 0, '& .MuiCheckbox-root': { pt: 0.5 } }}
                        />
                      </Box>
                    </Grid>
                  )}
                  {isOwnDepartment && !isAlreadyAssigned && formik.values.self_assign && (
                    <Grid item xs={12} sm={12}>
                      <Stack spacing={1.5}>
                        <Typography sx={{ fontWeight: 600, fontSize: 13, color: 'text.secondary' }}>
                          PMS Goal (required)
                        </Typography>
                        {!creatingNewGoal ? (
                          <>
                            <Autocomplete
                              options={assignableGoals || []}
                              loading={assignableGoalsLoading}
                              getOptionLabel={(option) => option.goal_name || ''}
                              value={selectedGoal}
                              onChange={(e, newVal) => setSelectedGoal(newVal)}
                              renderOption={(props, option) => (
                                <li {...props} key={option.goal_id}>
                                  <Box>
                                    <Typography fontSize={13}>{option.goal_name}</Typography>
                                    <Typography fontSize={11} color="text.secondary">
                                      Ends {option.goal_end_date}
                                    </Typography>
                                  </Box>
                                </li>
                              )}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Attach to goal"
                                  placeholder={assignableGoalsLoading ? 'Loading goals...' : 'Select one of your goals'}
                                  fullWidth
                                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                />
                              )}
                              isOptionEqualToValue={(option, value) => option.goal_id === value.goal_id}
                            />
                            <Button
                              size="small"
                              onClick={() => {
                                setSelectedGoal(null);
                                setCreatingNewGoal(true);
                              }}
                              sx={{ textTransform: 'none', fontSize: 12, alignSelf: 'flex-start', px: 0 }}
                            >
                              + Create a new goal instead
                            </Button>
                          </>
                        ) : (
                          <Stack spacing={1.5}>
                            <TextField
                              label="New goal name"
                              value={newGoalName}
                              onChange={(e) => setNewGoalName(e.target.value)}
                              fullWidth
                              size="small"
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                            <Stack direction="row" spacing={1.5}>
                              <TextField
                                label="Start date"
                                type="date"
                                value={newGoalStartDate}
                                onChange={(e) => setNewGoalStartDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                size="small"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                              />
                              <TextField
                                label="End date"
                                type="date"
                                value={newGoalEndDate}
                                onChange={(e) => setNewGoalEndDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: newGoalStartDate || undefined }}
                                fullWidth
                                size="small"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                              />
                            </Stack>
                            <GlobalMultiSelectId
                              label="Goal tags"
                              fullWidth
                              arrayState={newGoalTagIds}
                              arraySetState={setNewGoalTagIds}
                              data={
                                Array.isArray(allTags) ? allTags.filter((tag) => Number(tag?.appraisal_tag_status) === 1) : []
                              }
                              keys={{
                                id: 'appraisal_tag_name',
                                value: 'appraisal_tag_id',
                              }}
                            />
                            {assignableGoals?.length > 0 && (
                              <Button
                                size="small"
                                onClick={() => setCreatingNewGoal(false)}
                                sx={{ textTransform: 'none', fontSize: 12, alignSelf: 'flex-start', px: 0 }}
                              >
                                Use an existing goal instead
                              </Button>
                            )}
                          </Stack>
                        )}
                      </Stack>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={12}>
                    <FormControl fullWidth>
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={formik.values.category || ''}
                        label="Category"
                        onChange={(e) => formik.setFieldValue('category', e.target.value)}
                        sx={{ borderRadius: 2 }}
                      >
                        <MenuItem value="">No Category</MenuItem>
                        {taskCategories &&
                          taskCategories.length > 0 &&
                          taskCategories.map((cat) => (
                            <MenuItem key={cat.task_category_id} value={cat.task_category_id}>
                              {cat.task_category_name ?? cat.category_name ?? cat.name}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <GlobalMultiSelectId
                      label="Tags"
                      fullWidth
                      arrayState={formik.values.tagIds}
                      arraySetState={(val) => formik.setFieldValue('tagIds', val)}
                      data={Array.isArray(allTags) ? allTags.filter((tag) => Number(tag?.appraisal_tag_status) === 1) : []}
                      keys={{
                        id: 'appraisal_tag_name',
                        value: 'appraisal_tag_id',
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Priority</InputLabel>
                      <Select
                        value={formik.values.priority}
                        label="Priority"
                        onChange={(e) => formik.setFieldValue('priority', e.target.value)}
                        sx={{ borderRadius: 2 }}
                      >
                        {priorities.map((priority) => (
                          <MenuItem key={priority.approval_priority_id} value={priority.approval_priority_id}>
                            {priority.approval_priority_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Due Date"
                      type="date"
                      fullWidth
                      value={formik.values.expected_delivery_date}
                      onChange={(e) => formik.setFieldValue('expected_delivery_date', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        ...(selectedGoalStartDate ? { min: selectedGoalStartDate } : {}),
                        ...(selectedGoalEndDate ? { max: selectedGoalEndDate } : {}),
                      }}
                      error={Boolean(
                        formik.values.expected_delivery_date &&
                          ((selectedGoalStartDate && formik.values.expected_delivery_date < selectedGoalStartDate) ||
                            (selectedGoalEndDate && formik.values.expected_delivery_date > selectedGoalEndDate))
                      )}
                      helperText={
                        selectedGoalStartDate && selectedGoalEndDate
                          ? `Must be between ${selectedGoalStartDate} and ${selectedGoalEndDate} (the selected goal's date range)`
                          : selectedGoalEndDate
                          ? `Must be on or before ${selectedGoalEndDate} (the selected goal's end date)`
                          : ' '
                      }
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                </Grid>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 3 }}>
                <Stack direction="row" spacing={1}>
                  <Button onClick={() => setOpen(false)} sx={{ textTransform: 'none', color: '#888' }}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={
                      loading ||
                      (isOwnDepartment && !isAlreadyAssigned && formik.values.self_assign && !hasValidGoalSelection)
                    }
                    sx={{
                      background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
                      borderRadius: 2,
                      px: 3,
                      textTransform: 'none',
                      fontWeight: 700,
                      '&:hover': { background: 'linear-gradient(135deg, #5558E6, #7C4FE0)' },
                    }}
                  >
                    {loading ? 'Updating...' : 'Update Task'}
                  </Button>
                </Stack>
              </Box>
            </Form>
          </FormikProvider>
        </>
      }
    />
  );
};

export default EditTask;
